<template>
  <div class="dashboard-container">
    <div class="dashboard-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1 class="mb-1">Welcome back, {{ profile.first_name }}!</h1>
          <p class="text-muted mb-0">{{ store.name }} - {{ new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) }}</p>
        </div>
        <div v-if="activeShift" class="alert alert-info mb-4">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <i class="bi bi-info-circle me-2"></i>
              <strong>Active Shift:</strong> {{ activeShift.reference }} <br>
              &nbsp;&nbsp;<strong>Started at:</strong> {{ activeShift.started_at }}&nbsp;
            </div>
            <button style="margin-left: 10px; padding: 10px 20px;" class="btn btn-sm btn-outline-primary" @click="viewShiftDetails(activeShift.id)">
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="quick-actions mb-5">
      <h5 class="section-title mb-3">Quick Actions</h5>
      <div class="row g-3">
        <div class="col-6 col-sm-4 col-lg-3" @click="$router.push('/pos')">
          <div class="quick-action-card bg-white">
            <div class="icon-wrapper bg-primary">
              <vue-feather type="shopping-cart" size="24" stroke="#ffffff"></vue-feather>
            </div>
            <div class="action-text">Point of Sale</div>
            <div class="text-muted small mt-1">Make a new sale</div>
          </div>
        </div>
        <div class="col-6 col-sm-4 col-lg-3" @click="$router.push('/products')">
          <div class="quick-action-card bg-white">
            <div class="icon-wrapper bg-success">
              <vue-feather type="package" size="24" stroke="#ffffff"></vue-feather>
            </div>
            <div class="action-text">View Products</div>
            <div class="text-muted small mt-1">Browse product catalog</div>
          </div>
        </div>
        <div class="col-6 col-sm-4 col-lg-3" @click="$router.push('/store-products')">
          <div class="quick-action-card bg-white">
            <div class="icon-wrapper bg-info">
              <vue-feather type="layers" size="24" stroke="#ffffff"></vue-feather>
            </div>
            <div class="action-text">Stock Levels</div>
            <div class="text-muted small mt-1">Check inventory</div>
          </div>
        </div>
        <div class="col-6 col-sm-4 col-lg-3" @click="$router.push('/store-sales')">
          <div class="quick-action-card bg-white">
            <div class="icon-wrapper bg-warning">
              <vue-feather type="trending-up" size="24" stroke="#ffffff"></vue-feather>
            </div>
            <div class="action-text">My Sales</div>
            <div class="text-muted small mt-1">View sales history</div>
          </div>
        </div>
      </div>
    </div>

    <div class="product-performance">
      <div class="row g-4">
        <div class="col-lg-12">
          <div class="table-card h-100">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Popular Products</h5>
              <select class="form-select form-select-sm" v-model="popularityCount" style="max-width: 120px;" data="">
                <option v-for="count in [5, 10, 25]"
                        :key="count"
                        :value="count"
                        :selected="popularityCount === count">
                  {{ count }} Products
                </option>
              </select>
            </div>
            <div class="table-responsive">
              <table class="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>PRODUCT</th>
                    <th class="text-end">SOLD</th>
                  </tr>
                </thead>
                <tbody v-if="!loading">
                  <tr v-for="(product, index) in popularity" :key="index" class="product-row">
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="product-avatar me-2">
                          <span class="avatar-text">{{ product.name.charAt(0).toUpperCase() }}</span>
                        </div>
                        <div>
                          <div class="fw-medium">{{ product.name }}</div>
                          <small class="text-muted">{{ product.sku }}</small>
                        </div>
                      </div>
                    </td>
                    <td class="text-end">
                      <div class="fw-medium">{{ product.quantity }}</div>
                      <small class="text-muted">units</small>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="2" class="text-center py-4">
                      <Spinner spinner-class="mb-2" />
                      <div>Loading products...</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import analyticsService from "@/api/analyticsService";
import {decrypt} from "@/utils/crypto";
import formatDate from "@/utils/formatDate";
import router from "@/router";

export default {
  name: "SimpleDashboard",
  data() {
    return {
      loading: true,
      store: {},
      profile: {},
      activeShift: null,
      popularity: [],
      popularityCount: 10,
      todaySales: 0,
      totalProducts: 0,
    }
  },

  methods: {
    formatDate,
    async getBasicAnalytics() {
      this.popularity = await analyticsService.getProductPopularity(this.popularityCount)
      const analytics = await analyticsService.searchStoreAnalytics({store_id: this.store.id})
      this.todaySales = analytics.count ?? 0
      this.totalProducts = analytics.count ?? 0
    },
    loadActiveShift() {
      const cachedShift = sessionStorage.getItem('active_shift')
      if (cachedShift) {
        this.activeShift = JSON.parse(decrypt(cachedShift))
      }
    },
    viewShiftDetails(id) {
      router.push('/shift/' + id)
    },
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    this.loadActiveShift()
    await this.getBasicAnalytics()
    this.loading = false
  },

  watch: {
    popularityCount: async function () {
      this.popularity = await analyticsService.getProductPopularity(this.popularityCount)
    },
  }
}
</script>

<style scoped>
.dashboard-container {
  padding: 1.5rem;
  max-width: 1600px;
  margin: 0 auto;
  background-color: #f8fafc;
}

.dashboard-header {
  background: #fff;
  padding: 1.75rem 2rem;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
}

.dashboard-header h1 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.section-title {
  font-weight: 600;
  color: #1f2937;
  position: relative;
  padding-bottom: 0.75rem;
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
}

.section-title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 50px;
  height: 4px;
  background: #4361ee;
  border-radius: 2px;
}

.quick-actions {
  margin-bottom: 2rem;
}

.quick-action-card {
  padding: 1.5rem 1rem;
  border-radius: 14px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.quick-action-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03);
}

.quick-action-card .icon-wrapper {
  width: 60px;
  height: 60px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
}

.quick-action-card:hover .icon-wrapper {
  transform: scale(1.1);
}

.quick-action-card .action-text {
  font-weight: 600;
  font-size: 1rem;
  color: #000000;
}

.stats-overview {
  margin-bottom: 2rem;
}

.stats-card {
  background: #fff;
  border-radius: 16px;
  padding: 1.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;
  height: 100%;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.stats-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03);
}

.stats-card .icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
}

.stats-card .stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  color: #111827;
  margin: 0.25rem 0 0.25rem;
}

.stats-card .stat-label {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.product-performance {
  margin-bottom: 2rem;
}

.table-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  height: 100%;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.table-card .card-header {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 1.25rem 1.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-card .card-header h5 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.table {
  margin-bottom: 0;
  font-size: 0.9rem;
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.75rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 1.25rem 1.75rem;
  vertical-align: middle;
  border-color: #e5e7eb;
  color: #4b5563;
}

.table tbody tr:hover {
  background-color: #f9fafb;
}

.product-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in {
  animation: fadeIn 0.4s ease-out forwards;
  opacity: 0;
}

.bg-primary-light {
  background-color: #eef2ff;
}

.bg-info-light {
  background-color: #dbeafe;
}

@media (max-width: 768px) {
  .dashboard-container {
    padding: 1rem;
  }

  .dashboard-header {
    padding: 1.25rem;
  }

  .quick-action-card {
    padding: 1.25rem 0.5rem;
  }

  .stats-card {
    padding: 1.25rem;
  }
}
</style>
